import type { LcOperationMode } from './admin'
import { apiClient } from './client'
import type { ApiResponse } from '@/types/api'

export type AdminTestCompletionRunStatus =
  | 'QUEUED'
  | 'RUNNING'
  | 'CANCEL_REQUESTED'
  | 'COMPLETED'
  | 'STOPPED'
  | 'PAUSED'
  | 'FAILED'
  | 'INTERRUPTED'

export interface AdminTestCompletionRunHistoryItem {
  id: number
  learnerName: string
  operatorName: string
  endpointTitle: string
  status: AdminTestCompletionRunStatus
  totalCount: number
  successCount: number
  failedCount: number
  failureCode: string | null
  failureDetails?: Record<string, unknown> | null
  createdAt: string
  reset: { actorName: string; reason: string; completedAt: string } | null
}

export interface LcOperationModeUpdateRequest {
  mode: LcOperationMode
}

export interface LearningDataResetPreview {
  organizationId: number
  learnerCount: number
  counts: Record<string, number>
  total: number
}

function requireData<T>(response: { data: ApiResponse<T> }, message: string): T {
  if (response.data.data === undefined || response.data.data === null) throw new Error(message)
  return response.data.data
}

export const lcTestCompletionAdminApi = {
  updateOperationMode: async (organizationId: number, mode: LcOperationMode) =>
    requireData(
      await apiClient.patch<ApiResponse<{ organizationId: number; mode: LcOperationMode }>>(
        `/admin/organizations/${organizationId}/lc-operation-mode`,
        { mode },
      ),
      'Failed to update LC operation mode',
    ),
  previewReset: async (organizationId: number) =>
    requireData(
      await apiClient.post<ApiResponse<LearningDataResetPreview>>(
        `/admin/organizations/${organizationId}/learning-data-reset/preview`,
      ),
      'Failed to preview center learning data reset',
    ),
  resetCenter: async (organizationId: number, reason: string) =>
    requireData(
      await apiClient.post<ApiResponse<LearningDataResetPreview>>(
        `/admin/organizations/${organizationId}/learning-data-reset`,
        { reason },
      ),
      'Failed to reset center learning data',
    ),
  history: async (organizationId: number) =>
    requireData(
      await apiClient.get<ApiResponse<AdminTestCompletionRunHistoryItem[]>>(
        `/admin/organizations/${organizationId}/test-completion-runs`,
      ),
      'Failed to load test completion run history',
    ),
}

export const TEST_COMPLETION_ERROR_MESSAGES: Record<string, string> = {
  TEST_COMPLETION_CENTER_REQUIRED: '테스트 센터에서만 자동 완료를 사용할 수 있습니다.',
  TEST_COMPLETION_SESSION_NOT_OPEN: '등원 중인 오늘 수업이 필요합니다.',
  TEST_COMPLETION_LEARNER_INELIGIBLE: '재원 및 활성 과정 상태를 확인해 주세요.',
  TEST_COMPLETION_RUN_ACTIVE: '이 센터에서 다른 자동 완료 작업이 진행 중입니다.',
  TEST_COMPLETION_PLAN_CHANGED: '학습 경로가 변경되었습니다. 다시 확인해 주세요.',
  TEST_COMPLETION_INVALID_ENDPOINT: '선택한 완료 범위를 사용할 수 없습니다.',
  TEST_COMPLETION_PREFLIGHT_BLOCKED: '자동 완료할 수 없는 콘텐츠가 포함되어 있습니다.',
  TEST_COMPLETION_WRITE_LOCKED: '자동 완료 중인 원생의 학습 데이터는 변경할 수 없습니다.',
  TEST_COMPLETION_REQUIRED_FILE_UNSUPPORTED: '필수 파일 제출 활동은 자동 완료할 수 없습니다.',
  TEST_COMPLETION_ITEM_FAILED: '항목 처리 중 오류가 발생해 작업이 중단되었습니다.',
  TEST_COMPLETION_OFFLINE_INPUT_REQUIRES_TEACHER: '채점 보류 퀴즈는 교사 최종 완료 방식이 필요합니다.',
  TEST_COMPLETION_QUIZ_DATA_MISSING: '퀴즈 컴포넌트에 출제 가능한 문제가 없습니다.',
  TEST_COMPLETION_QUIZ_ANSWER_MISSING: '자동 채점에 필요한 퀴즈 정답 데이터가 없습니다.',
  TEST_COMPLETION_VIDEO_DATA_MISSING: '영상 컴포넌트의 영상 데이터가 없습니다.',
  TEST_COMPLETION_VIDEO_DURATION_MISSING: '영상 자동 완료에 필요한 재생 시간이 없습니다.',
  TEST_COMPLETION_PAUSE_QUIZ_INVALID: '영상 중간 퀴즈 데이터가 올바르지 않습니다.',
  TEST_COMPLETION_TOON_DATA_MISSING: '깨봉툰 컴포넌트에 표시할 패널이 없습니다.',
  TEST_COMPLETION_WARMUP_DATA_MISSING: '생각열기 컴포넌트 콘텐츠가 없습니다.',
  TEST_COMPLETION_BUILDUP_DATA_MISSING: '빌드업 컴포넌트 콘텐츠가 없습니다.',
  TEST_COMPLETION_BUILDUP_DATA_INVALID: '빌드업 교사 채점 항목이 올바르지 않습니다.',
  TEST_COMPLETION_ESSENTIAL_CHECK_DATA_INVALID: '핵심 체크 항목이 올바르지 않습니다.',
  TEST_COMPLETION_INTERACTIVE_DATA_MISSING: '인터랙티브 컴포넌트 콘텐츠가 없습니다.',
  TEST_COMPLETION_TEACHER_FINALIZATION_FAILED: '교사 채점 또는 최종 완료 단계에서 오류가 발생했습니다.',
  TEST_COMPLETION_RESUME_REPLAN_REQUIRED: '경로가 변경되어 새 작업을 만들어야 합니다.',
  TEST_COMPLETION_RESET_REQUIRED: '상용 센터 전환 전에 센터 학습 데이터를 초기화해야 합니다.',
}
